import React, { useState } from 'react';
import { Typography, Breadcrumb, Card, Row, Col, Statistic, Table, Tag, Select, DatePicker, Space, Progress } from 'antd';
import { 
  LineChartOutlined, 
  RiseOutlined,
  DollarOutlined,
  EyeOutlined,
  ShoppingCartOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

const AnalyticsReports = () => {
  const [selectedCampaign, setSelectedCampaign] = useState('all');

  // Conversion data
  const [conversions] = useState([
    { id: 1, campaign: 'Summer Sale', impressions: 125000, clicks: 8500, conversions: 425, ctr: 6.8, cvr: 5.0, cost: 3200, revenue: 12750, roi: 298 },
    { id: 2, campaign: 'Brand Awareness', impressions: 250000, clicks: 12000, conversions: 360, ctr: 4.8, cvr: 3.0, cost: 5400, revenue: 10800, roi: 100 },
    { id: 3, campaign: 'Product Launch', impressions: 180000, clicks: 15000, conversions: 900, ctr: 8.3, cvr: 6.0, cost: 8500, revenue: 27000, roi: 218 },
    { id: 4, campaign: 'Holiday Special', impressions: 95000, clicks: 6200, conversions: 310, ctr: 6.5, cvr: 5.0, cost: 2400, revenue: 9300, roi: 288 },
  ]);

  // Conversion tracking columns
  const columns = [
    { 
      title: 'Campaign', 
      dataIndex: 'campaign', 
      key: 'campaign',
      render: (text) => <Text strong>{text}</Text>
    },
    { 
      title: 'Impressions', 
      dataIndex: 'impressions', 
      key: 'impressions',
      render: (num) => num.toLocaleString()
    },
    { 
      title: 'Clicks', 
      dataIndex: 'clicks', 
      key: 'clicks',
      render: (num) => num.toLocaleString()
    },
    { 
      title: 'Conversions', 
      dataIndex: 'conversions', 
      key: 'conversions',
      render: (num) => <Text strong style={{ color: '#52c41a' }}>{num.toLocaleString()}</Text>
    },
    { 
      title: 'CTR', 
      dataIndex: 'ctr', 
      key: 'ctr',
      render: (num) => `${num}%`
    },
    { 
      title: 'CVR', 
      dataIndex: 'cvr', 
      key: 'cvr',
      render: (num) => `${num}%`
    },
    { 
      title: 'Cost', 
      dataIndex: 'cost', 
      key: 'cost',
      render: (num) => `$${num.toLocaleString()}`
    },
    { 
      title: 'Revenue', 
      dataIndex: 'revenue', 
      key: 'revenue',
      render: (num) => <Text style={{ color: '#52c41a' }}>${num.toLocaleString()}</Text>
    },
    { 
      title: 'ROI', 
      dataIndex: 'roi', 
      key: 'roi',
      render: (num) => (
        <Tag color={num > 200 ? 'green' : num > 100 ? 'blue' : 'orange'}>
          {num}%
        </Tag>
      )
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Analytics & Reports' }]} />
      
      <div style={{ marginTop: 12, marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>Analytics & Reports</Title>
        <Paragraph type="secondary" style={{ marginTop: 8 }}>
          Track your campaign performance and conversion metrics
        </Paragraph>
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Text strong>Filters:</Text>
          </Col>
          <Col>
            <Select
              value={selectedCampaign}
              onChange={setSelectedCampaign}
              style={{ width: 200 }}
              options={[
                { value: 'all', label: 'All Campaigns' },
                { value: 'summer', label: 'Summer Sale' },
                { value: 'brand', label: 'Brand Awareness' },
                { value: 'product', label: 'Product Launch' },
                { value: 'holiday', label: 'Holiday Special' },
              ]}
            />
          </Col>
          <Col>
            <RangePicker />
          </Col>
        </Row>
      </Card>

      {/* Key Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Impressions"
              value={650000}
              prefix={<EyeOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <Progress percent={85} showInfo={false} strokeColor="#1890ff" />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Clicks"
              value={41700}
              prefix={<LineChartOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <Progress percent={72} showInfo={false} strokeColor="#722ed1" />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Conversions"
              value={1995}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#52c41a' }}
              suffix={
                <span style={{ fontSize: 14, color: '#52c41a' }}>
                  <RiseOutlined /> 12%
                </span>
              }
            />
            <Progress percent={68} showInfo={false} strokeColor="#52c41a" />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Revenue"
              value={59850}
              prefix="$"
              valueStyle={{ color: '#52c41a' }}
              suffix={
                <span style={{ fontSize: 14, color: '#52c41a' }}>
                  <RiseOutlined /> 18%
                </span>
              }
            />
            <Progress percent={92} showInfo={false} strokeColor="#52c41a" />
          </Card>
        </Col>
      </Row>

      {/* Performance Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Average CTR"
              value={6.6}
              suffix="%"
              prefix={<LineChartOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Click-through rate across all campaigns
            </Text>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Average CVR"
              value={4.8}
              suffix="%"
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Conversion rate across all campaigns
            </Text>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Average ROI"
              value={226}
              suffix="%"
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Return on investment across all campaigns
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Cost Analysis */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={12}>
          <Card title="Cost Breakdown">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text>Total Spend</Text>
                  <Text strong>$19,500</Text>
                </div>
                <Progress percent={100} strokeColor="#1890ff" />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text>Cost per Click (CPC)</Text>
                  <Text strong>$0.47</Text>
                </div>
                <Progress percent={65} strokeColor="#722ed1" />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text>Cost per Conversion</Text>
                  <Text strong>$9.77</Text>
                </div>
                <Progress percent={45} strokeColor="#52c41a" />
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Top Performing Campaigns">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <RiseOutlined style={{ color: '#52c41a' }} />
                  <Text>Product Launch</Text>
                </Space>
                <Tag color="green">ROI: 218%</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <RiseOutlined style={{ color: '#52c41a' }} />
                  <Text>Summer Sale</Text>
                </Space>
                <Tag color="green">ROI: 298%</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <RiseOutlined style={{ color: '#52c41a' }} />
                  <Text>Holiday Special</Text>
                </Space>
                <Tag color="green">ROI: 288%</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <LineChartOutlined style={{ color: '#1890ff' }} />
                  <Text>Brand Awareness</Text>
                </Space>
                <Tag color="blue">ROI: 100%</Tag>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Conversion Tracking Table */}
      <Card title="Conversion Tracking">
        <Table
          columns={columns}
          dataSource={conversions}
          rowKey="id"
          pagination={false}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default AnalyticsReports;
